import uuidv4 from 'uuid/v4';
import DeviceDetector from 'device-detector-js';
import {
  getDefaultRoomId,
  sendWsMessage,
  sendConnectionRequestMessage,
  sendUserQueryMessage,
  sendPing,
  respondPingMessage,
} from './ws-v1';
import { getAvaBackendEndpoint, clearSearchValue } from './http';
import { log } from './log';
import config from '../../electron/package.json';
import { getBrowserName, getBrowserVersion, getOS, getOSVersion } from './index';
import { handleScribeDashboardWsMessage } from '../actions/scribe-dashboard';
import { getSingletonWebRTCManager, recreateSingletonWebRTCManager } from './webrtc';

const isTest = process.env.NODE_ENV === 'test';
let socketId;
let socket;

const invalidate = socketId_ => {
  if (socketId !== socketId_) {
    return;
  }
  if (socket) {
    socket.close();
  }
  socket = undefined;
  socketId = undefined;
};

const handleWs = (ws, id) => {
  invalidate(socketId);
  socket = ws;
  socketId = id;
};

// Creates a new socket connected to socketUrl. If secondary is false, the socket
// created will be 'main' socket, returned by getSocket. Otherwise it will be a
// 'secondary' socket.
const createNewSocket = (socketUrl, secondary = false) =>
  new Promise((resolve, reject) => {
    const sId = uuidv4();
    if (isTest) return;
    const ws = new WebSocket(socketUrl);
    const logBackendErrors = ({ data }) => {
      const parsedData = JSON.parse(data);
      if (parsedData && parsedData.type === 'error') {
        // eslint-disable-next-line no-console
        log('ws-backend-error', 'error', parsedData);
      }
    };
    ws.onopen = () => {
      log('websocketStatus', 'info', {
        event: 'opened',
        url: socketUrl,
        success: true,
      });
      ws.addEventListener('message', logBackendErrors);
      if (!secondary) handleWs(ws, sId);
      resolve(ws);
    };
    ws.onerror = error => {
      log('websocketStatus', 'error', {
        event: 'error',
        url: socketUrl,
        success: true,
        error,
      });
      if (!secondary) invalidate(sId);
      clearSearchValue(window, 'token');
      reject(new Error('Websocket error'), { cause: error });
    };
    ws.onclose = res => {
      log('websocketStatus', 'info', {
        event: 'closed',
        url: socketUrl,
        success: true,
        reason: res.reason,
        code: res.code,
      });
      clearSearchValue(window, 'token');

      ws.removeEventListener('message', logBackendErrors);
      if (!secondary) invalidate(sId);
    };
  });

export const getSocket = () => {
  if (isTest) {
    return { send: () => {} };
  }
  if (socket) return socket;
  throw new Error('no socket found.');
};

export const getSocketIfExist = () => socket;
export const getSocketId = () => socketId;
export default getSocket;

export const createScribeSocket = async (url, handleScribeDashboardMessage) => {
  const socket = await createNewSocket(url, true);
  socket.addEventListener('message', data => {
    const message = JSON.parse(data.data);
    handleScribeDashboardMessage(message);
  });
  return socket;
};

export const createV1Socket = async (url, user, options) => {
  let ping;
  const { roomId, request, scribe: scribeOption } = options;
  const scribe = user.features.scribe && scribeOption;
  await createNewSocket(url);
  const defaultRoomId = getDefaultRoomId(user.uid || user.id);
  let lastRoomId = roomId || defaultRoomId;
  window.onunload = () => {
    clearInterval(ping);
    if (socket) {
      socket.send(
        JSON.stringify({
          type: 'connection-params-update',
          roomId: getDefaultRoomId(user.uid || user.id),
        })
      );
    }
  };
  socket.v1 = true;
  // Lifetime of the RTC connection is tied to the WS lifetime. It will be freed
  // when the socket reconnects, or when the whole app closes. We do not close
  // the webRTC connection when the socket closes, so that the audio has chance to
  // flow even when the socket is unstable.
  recreateSingletonWebRTCManager();

  socket.addEventListener('message', message => {
    const data = JSON.parse(message.data);
    if (data.type === 'webrtc') {
      // WebRTCManager can handle messages even before it is initialized.
      getSingletonWebRTCManager().handleV1Message(data);
    }
    if (data.type === 'connection-succeeded') {
      ping = setInterval(() => {
        sendPing(socket);
      }, 1000 * 10);
      if (scribe) {
        // We only allow the WebRTCManager to send data after the server
        // signals that it's ready with connection-succeeded.
        getSingletonWebRTCManager().initialize(message => {
          sendWsMessage(socket, {
            type: 'webrtc',
            ...message,
          });
        });
        return;
      }
    }
    if (data.type === 'PING') {
      respondPingMessage(socket, data);
    }
    if (data.type === 'room-status' || data.type === 'room-status-update') {
      if (data.type === 'room-status' && data.id && data.id !== lastRoomId) {
        if (scribe) {
          sendWsMessage(socket, {
            type: 'connection-params-update',
            roomId: data.id,
            downAudio: {
              speakerId: data.audioStreams[0]?.avaId,
              trackId: data.audioStreams[0]?.trackId,
            },
          });
        }
      }
    }
  });

  const deviceDetector = new DeviceDetector();
  const deviceInfo = deviceDetector.parse(window.navigator.userAgent);

  const type = !window.isElectron ? 'web' : 'desktop';
  const appVersion = config.version;

  const system = !window.isElectron ? deviceInfo?.os?.name : getOS();

  const osString = deviceInfo?.os?.version.length > 0 ? deviceInfo?.os?.version : 'Unknown';
  const operationSystem = !window.isElectron ? osString : getOSVersion() || 'Unknown';

  const browserName = !window.isElectron ? deviceInfo?.client?.name : getBrowserName();
  const browserVersion = !window.isElectron ? deviceInfo?.client?.version : getBrowserVersion() || 'Unknown';

  const deviceString = `${deviceInfo?.device?.brand} ${deviceInfo?.device?.model}`.trim();
  const device = !window.isElectron && deviceString.length > 0 ? deviceString : 'Unknown';

  const client = {
    type,
    appVersion,
    system,
    os: operationSystem,
    device,
    browserName,
    browserVersion,
  };

  socket.send(
    JSON.stringify({
      type: 'connection-params',
      speakerId: user.parse.avaId,
      roomId: roomId || getDefaultRoomId(user.uid || user.id),
      username: user.parse.userName,
      client,
      downAudio: scribe
        ? {
            host: true,
          }
        : undefined,
      conversationMode: 'public',
    })
  );
  if (request && request.avaName) {
    const fromSource = 'default';
    sendConnectionRequestMessage(socket, fromSource, { avaName: request.avaName });
  }
};
